<template>
  <div
    class="product-item-add-form"
    :class="{ 'product-item-add-form--adding': addingProductToCart, 'product-item-add-form--open': show, 'product-item-add-form--bundle': customisableBundle }"
  >
    <div class="product-item-add-form__mobile-wrapper">
      <div class="flex md:hidden items-center product-item-add-form__header">
        <h3 class="product-item-add-form__product-title">
          {{ product.title }}
        </h3>
        <a
          :href="url"
          class="underline xs font-semibold"
        >
          More details
        </a>
      </div>

      <button
        class="close-btn product-item-add-form__close"
        data-close
        @click="closeAddForm"
      >
        Close
        <CloseIcon />
      </button>
      <template v-if="!customisableBundle">
        <h2
          v-if="collectionTemplate"
          class="product-item-add-form__title"
        >
          {{ title }}
        </h2>
        <h3
          v-else
          class="product-item-add-form__title"
        >
          {{ title }}
        </h3>
        <ul
          v-if="flavourCollection"
          class="product-item-add-form__options--variant-products product-item-add-form__options flex overflow-auto"
        >
          <li
            v-for="flavourCollectionProduct in flavourCollection.products"
            :key="`${flavourCollectionProduct.handle}flav-col-prod`"
            class="product-option-group__value"
          >
            <label
              :for="`${product.handle}-${flavourCollectionProduct.variants[0].id}`"
              class="product-option-group__value-cta"
            >
              <input
                :id="`${product.handle}-${flavourCollectionProduct.variants[0].id}`"
                v-model="currentSelectedVariant"
                :value="flavourCollectionProduct.variants[0]"
                type="radio"
                :checked="currentSelectedVariant.id === flavourCollectionProduct.variants[0].id"
                :disabled="!flavourCollectionProduct.variants[0].available"
              >
              <span>
                <div class="product__flavour-collection-list-item-image">
                  <img
                    :src="flavourCollectionProduct.image"
                    :alt="`${ flavourCollectionProduct.title }-image`"
                    width="34"
                  >
                </div>
                <h3 class="product__flavour-collection-list-item-title xs">
                  {{ flavourCollectionProduct.title }}
                </h3>
              </span>
            </label>
          </li>
        </ul>
        <ul
          v-else-if="product.variants.length > 1"
          class="product-item-add-form__options flex overflow-auto"
        >
          <li
            v-for="variant in product.variants"
            :key="`${product.handle}-${variant.id}`"
            class="product-option-group__value"
          >
            <label
              :for="`${product.handle}-${variant.id}`"
              class="product-option-group__value-cta"
            >
              <input
                :id="`${product.handle}-${variant.id}`"
                v-model="currentSelectedVariant"
                :value="variant"
                type="radio"
                :checked="currentSelectedVariant.id === variant.id"
                :disabled="!variant.available"
              >
              <span>
                {{ variant.title }}
              </span>
            </label>
          </li>
        </ul>
      </template>
      <button
        class="product-item-add-form__add btn"
        :class="{ 'btn--black': availableToPurchase, 'btn--grey': !availableToPurchase, 'product-item-add-form__add--adding': addingProductToCart }"
        :disabled="!availableToPurchase"
        @click="addToCart($event)"
      >
        {{ addToCartText }}
      </button>
      <a
        v-if="customisableBundle"
        class="product-item-add-form__customise btn btn--border-red"
        :href="url"
      >
        Customise pack
      </a>
    </div>
  </div>
</template>

<script>
import { useCartStore } from '../pinia/CartStore'
import { mapActions } from 'pinia'

import { productPrice } from '../helpers/product'
import { getSizedImageUrl } from '@shopify/theme-images'

import CloseIcon from '../icons/close-icon'

export default {
  components: {
    CloseIcon
  },
  props: {
    closeAddForm: {
      type: Function,
      default: () => {
        return null
      }
    },
    show: {
      type: Boolean,
      default: false
    },
    collectionTemplate: {
      type: Boolean,
      default: false
    },
    customisableBundle: {
      type: Object,
      default: null
    },
    product: {
      type: Object,
      default: null
    },
    hasVariants: {
      type: Boolean,
      default: false
    },
    flavourCollectionHandle: {
      type: String,
      default: null
    },
    url: {
      type: String,
      default: null
    },
  },
  data () {
    return {
      flavourCollections: [...window.theme.flavourCollections],
      strings: { ...window.theme.strings.product },
      currentSelectedVariant: this.product.variants.find(variant => variant.available) || this.product.variants[0],
      addingProductToCart: false,
      addingToCartText: 'Adding to cart...',
    }
  },
  computed: {
    flavourCollection () {
      if (this.flavourCollectionHandle) {
        return this.flavourCollections.find(collection => collection.handle === this.flavourCollectionHandle)
      }
      return null
    },

    title () {
      return this.flavourCollection ? 'Select serving' : 'Select'
    },
    addToCartText () {
      if (this.soldOut) {
        return this.strings.soldOut
      } else {
        if (this.addingProductToCart) {
          return this.addingToCartText
        } else {
          return this.defaultAddToCartText
        }
      }
    },
    defaultAddToCartText () {
      return this.strings.addToCart.replace('{{ price }}', this.price)
    },
    price () {
      return this.customisableBundle ? productPrice(this.customisableBundle.price) : productPrice(this.currentSelectedVariant.price)
    },
    soldOut () {
      return !this.currentSelectedVariant.available
    },
    availableToPurchase () {
      return !this.soldOut
    },
    payload () {
      return {
        items: [
          {
            id: this.currentSelectedVariant.id,
            quantity: 1
          }
        ]
      }
    },
    /*
      Bundles
    */
    bundlePayload () {
      /*
        Get default product selections
      */
      const bundle = { ...this.customisableBundle }
      this.customisableBundle.collections.forEach((collection, index) => {
        const availableProducts = collection.products.filter(product => product.available)
        const defaultBundleProducts = []
        while (defaultBundleProducts.length < collection.minimumQuantity) {
          availableProducts.forEach(product => {
            if (defaultBundleProducts.length < collection.minimumQuantity) {
              defaultBundleProducts.push(product)
            } else {
              return
            }
          })
        }
        bundle.collections[index].selectedProducts = [...defaultBundleProducts]
      })

      const selectedProducts = []
      bundle.collections.forEach(collection => selectedProducts.push([]))

      bundle.collections.forEach((collection, index) => {
        collection.selectedProducts.forEach(product => {
          const alreadyAddedProduct = selectedProducts[index].find(selectedProduct => selectedProduct.variantId === product.variantId)
          if (alreadyAddedProduct) {
            alreadyAddedProduct.quantity += 1
          } else {
            selectedProducts[index].push(
              {
                title: product.title,
                variantId: product.variantId,
                quantity: 1,
                productType: collection.productType
              }
            )
          }
        })
      })

      /*
        Setup payload
      */
      const payload = {
        items: [
          {
            id: this.customisableBundle.product.variantId,
            quantity: 1,
            properties: {}
          }
        ]
      }

      /*
        Update bundle properties with each product
      */
      bundle.collections.forEach((collection, collectionIndex) => {
        collection.selectedProducts.forEach((product, i) => {
          /*
            If duo of two different type of tubs
            update index of second collection item
          */
          if (this.isDuo && bundle.collections.length === 2 & collectionIndex === 1) {
            i = 1
          }
          payload.items[0].properties[`${collection.productType} ${i + 1}`] = product.handle
        })
      })

      /*
        Add each product to payload
      */
      selectedProducts.forEach(collection => {
        collection.forEach(product => {
          payload.items.push({
            id: product.variantId,
            quantity: product.quantity,
            properties: {
              bundle: bundle.product.handle
            }
          })
        })
      })

      return payload
    }
  },
  mounted () {
  },
  methods: {
    ...mapActions(useCartStore, [
      'addItems',
    ]),

    async addToCart () {
      if (this.availableToPurchase) {
        this.addingProductToCart = true
        if (this.customisableBundle) {
          await this.addItems(this.bundlePayload)
        } else {
          await this.addItems(this.payload)
        }
        this.closeAddForm()
        this.addingProductToCart = false
      }
    },
  }
}
</script>
